export type LetterTemplateId =
    | 'letter-formel'
    | 'letter-classic'
    | 'letter-corporate'
    | 'letter-modern'
    | 'letter-ivoire';

export interface LetterAnswers {
    creationMode: 'new' | 'from-cv';
    experienceLevel: string;
    studying: boolean;
    hasDegree: boolean;
    degree: string;
    fieldOfStudy: string;
    sector: string;
    hasTargetJob: boolean;
    jobTitle: string;
    company: string;
    city: string;
    lastJobTitle: string;
    qualities: string[];
    hasGap: boolean;
    gapReason: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    address: string;
    additionalContext: string;
}

export interface LetterContent {
    subject: string;
    recipient: string;
    greeting: string;
    introduction: string;
    body: string;
    gap: string;
    invitation: string;
    conclusion: string;
    signature: string;
    /** PNG data URL de la signature manuscrite */
    signatureImage: string;
}

export interface LetterStyle {
    primaryColor: string;
    fontFamily: string;
}

export interface LetterData {
    _id?: string;
    userId?: string;
    template: LetterTemplateId | string;
    title: string;
    sourceCvId?: string | null;
    answers: LetterAnswers;
    content: LetterContent;
    style: LetterStyle;
    aiMeta?: { provider?: string; generatedAt?: string | null };
    wizardStep?: number;
    status?: 'draft' | 'generated' | 'final';
    createdAt?: string;
    updatedAt?: string;
}

export const EMPTY_ANSWERS: LetterAnswers = {
    creationMode: 'new',
    experienceLevel: '',
    studying: false,
    hasDegree: false,
    degree: '',
    fieldOfStudy: '',
    sector: '',
    hasTargetJob: false,
    jobTitle: '',
    company: '',
    city: '',
    lastJobTitle: '',
    qualities: [],
    hasGap: false,
    gapReason: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    additionalContext: '',
};

export const EMPTY_CONTENT: LetterContent = {
    subject: '',
    recipient: '',
    greeting: '',
    introduction: '',
    body: '',
    gap: '',
    invitation: '',
    conclusion: '',
    signature: '',
    signatureImage: '',
};

export const EXPERIENCE_LEVELS = [
    'Pas d’expérience',
    'Moins de 3 ans',
    '3 à 5 ans',
    '6 à 10 ans',
    '10 ans et plus',
];

export const SECTORS = [
    'Banque & Finance',
    'Commerce & Distribution',
    'Informatique & Télécoms',
    'BTP & Immobilier',
    'Santé & Pharmacie',
    'Administration & Fonction publique',
    'ONG & Développement',
    'Éducation & Formation',
    'Transport & Logistique',
    'Hôtellerie & Tourisme',
    'Agroalimentaire',
    'Énergie & Mines',
    'Marketing & Communication',
    'Juridique',
    'Autre',
];

export const QUALITIES = [
    'Efficacité',
    'Ouverture d’esprit',
    'Flexibilité',
    'Capacité d’apprentissage',
    'Résilience',
    'Communication',
    'Négociation',
    'Leadership',
    'Collaboration',
    'Pensée analytique',
    'Résolution de problèmes',
    'Orienté résultats',
    'Dynamisme',
    'Rigueur',
    'Sens du service',
];

export const GAP_REASONS = [
    'En raison de la pandémie',
    'Recherche d’emploi',
    'Problèmes de santé (personnels ou familiaux)',
    'Voyage',
    'Parent au foyer',
    'Études / Formation',
    'Déménagement',
    'Congé sabbatique',
];

export const CITIES_CI = [
    'Abidjan',
    'Bouaké',
    'Yamoussoukro',
    'San-Pédro',
    'Korhogo',
    'Daloa',
    'Man',
    'Gagnoa',
    'Autre',
];

export const LETTER_TEMPLATES = [
    {
        id: 'letter-formel' as const,
        name: 'Classique standard',
        hex: '#111827',
        description: 'Mise en page française classique (expéditeur / destinataire)',
    },
    {
        id: 'letter-classic' as const,
        name: 'Classique Ivoire',
        hex: '#0d9488',
        description: 'Sobre et élégant, idéal candidatures formelles',
    },
    {
        id: 'letter-corporate' as const,
        name: 'Corporate Abidjan',
        hex: '#1e3a5f',
        description: 'En-tête structuré pour grandes entreprises',
    },
    {
        id: 'letter-modern' as const,
        name: 'Moderne Plateau',
        hex: '#c2410c',
        description: 'Accent coloré, style contemporain',
    },
    {
        id: 'letter-ivoire' as const,
        name: 'Élégance CI',
        hex: '#166534',
        description: 'Vert & or, identité ivoirienne discrète',
    },
];

export type LetterSectionKey = Exclude<keyof LetterContent, 'signatureImage'>;

export const CONTENT_SECTIONS: { key: LetterSectionKey; label: string }[] = [
    { key: 'subject', label: 'Objet' },
    { key: 'recipient', label: 'Destinataire / service' },
    { key: 'greeting', label: 'Formule de politesse' },
    { key: 'introduction', label: 'Introduction' },
    { key: 'body', label: 'Corps de la lettre' },
    { key: 'gap', label: 'Périodes d’inactivité' },
    { key: 'invitation', label: 'Formule d’invitation' },
    { key: 'conclusion', label: 'Conclusion' },
    { key: 'signature', label: 'Signature' },
];

export const DEFAULT_RECIPIENT = 'Service des ressources humaines';
